// @ts-nocheck

import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Typography,
  Grid,
  Button,
  Theme,
  Hidden,
  Switch,
  Tooltip,
  Checkbox,
  Link,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import LocalVideoPreview from './LocalVideoPreview/LocalVideoPreview';
import SettingsMenu from './SettingsMenu/SettingsMenu';
import { Steps } from '../PreJoinScreens';
import ToggleAudioButton from '../../Buttons/ToggleAudioButton/ToggleAudioButton';
import ToggleVideoButton from '../../Buttons/ToggleVideoButton/ToggleVideoButton';
import { useAppState } from '../../../state';
import useChatContext from '../../../hooks/useChatContext/useChatContext';
import useVideoContext from '../../../hooks/useVideoContext/useVideoContext';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useKrispToggle } from '../../../hooks/useKrispToggle/useKrispToggle';
import SmallCheckIcon from '../../../icons/SmallCheckIcon';
import InfoIconOutlined from '../../../icons/InfoIconOutlined';
import { getCookie } from '../../../utils';
import PolicyPopup from '../PolicyPopup';

const useStyles = makeStyles((theme: Theme) => ({
  gutterBottom: {
    marginBottom: '1em',
  },
  marginTop: {
    marginTop: '1em',
  },
  deviceButton: {
    width: '100%',
    border: '2px solid #aaa',
    margin: '1em 0',
  },
  localPreviewContainer: {
    paddingRight: '2em',
    marginBottom: '2em',
    [theme.breakpoints.down('sm')]: {
      padding: '0 2.5em',
    },
  },
  joinButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
      width: '100%',
      '& button': {
        margin: '0.5em 0',
      },
    },
  },
  mobileButtonBar: {
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '1.5em 0 1em',
    },
  },
  mobileButton: {
    padding: '0.8em 0',
    margin: 0,
  },
  toolTipContainer: {
    display: 'flex',
    alignItems: 'center',
    '& div': {
      display: 'flex',
      alignItems: 'center',
    },
    '& svg': {
      marginLeft: '0.3em',
    },
  },
}));

interface DeviceSelectionScreenProps {
  name: string;
  roomName: string;
  roomDetails: any;
  startVideoPreview: () => void;
  setTwilioToken: (token: string) => void;
  setShowVideoPreview: (val: boolean) => void;
}

export default function DeviceSelectionScreen({
  name,
  roomName,
  roomDetails,
  startVideoPreview,
  setTwilioToken,
  setShowVideoPreview,
}: DeviceSelectionScreenProps) {
  const classes = useStyles();
  const {
    getToken,
    isFetching,
    isKrispEnabled,
    isKrispInstalled,
    updateRoomDetails,
    fetchPolicyById,
    logMeetingJoin,
  } = useAppState();
  const { connect: chatConnect } = useChatContext();
  const { connect: videoConnect, isAcquiringLocalTracks, isConnecting } = useVideoContext();
  const { toggleKrisp } = useKrispToggle();
  const [isPopupOpen, setIsPopupOpen] = useState(false); //to open or close policy popup
  const [hasConsent, setHasConsent] = useState(false); //to check consent is checked or not
  const [policyContent, setPolicyContent] = useState([]); //set policy list assign to given meet
  const [policyData, setPolicyData] = useState(null); //set selected policy data
  const disableButtons = isFetching || isAcquiringLocalTracks || isConnecting || !hasConsent;

  const handleJoin = async () => {
    try {
      const tokenResponse: any = await getToken(name, roomName);

      if (!tokenResponse?.token) throw 'token undefined (after call getToken)';

      const uid: any = getCookie('vuid');
      console.log('handle join uid : ', uid);

      if (roomDetails?.userId == uid && roomDetails?.expired) {
        await updateRoomDetails({ expired: false, roomname: roomName });
      }

      setTwilioToken(tokenResponse.token);

      const urlParams: any = new URLSearchParams(window.location.search);
      const isAdmin: any = urlParams.get('a');
      logMeetingJoin({ name: name, meet_id: roomName }); //logged user joining time
      if (isAdmin || !roomDetails?.video_path) {
        console.log('🎯 Admin detected. Skipping preview and joining room...');
        await videoConnect(tokenResponse.token);
        if (process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true') {
          chatConnect(tokenResponse.token);
        }
      } else {
        console.log('🙋 Regular user, showing video preview...');
        setShowVideoPreview(true);
      }
    } catch (error) {
      console.error('❌ Error during join process:', error);
    }
  };
  const handleOpenPolicy = async (policyDataContent: any) => {
    setPolicyData(policyDataContent);
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  useEffect(() => {
    if (roomDetails.policy_ids) {
      fetchPolicyById(roomDetails.policy_ids).then(data => {
        setPolicyContent(data);
      });
      // If policy_ids exists, you might want to set false initially or keep as is
      // Only set once if hasConsent hasn’t been explicitly changed
      setHasConsent(prev => (prev === null || prev === undefined ? false : prev));
    }
  }, [roomDetails]);

  if (isFetching || isConnecting || roomDetails.loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" direction="column" style={{ height: '100%' }}>
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            Joining Meeting
          </Typography>
        </div>
      </Grid>
    );
  }

  const urlParams: any = new URLSearchParams(window.location.search);
  const isAdmin: any = urlParams.get('a');
  console.log('DeviceSelectionScreen roomDetails:', roomDetails);
  if (!roomDetails.loading && !roomDetails.allowToJoin && !roomDetails.expired && !isAdmin) {
    return (
      <Grid container justifyContent="center" alignItems="center" direction="column" style={{ height: '100%' }}>
        <div>
          <CircularProgress variant="indeterminate" />
        </div>
        <div>
          <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px' }}>
            Waiting for the invitation to be accepted. If you are an admin user, please log in to your deposyt account
            and try again.
          </Typography>
        </div>
      </Grid>
    );
  }

  if (roomDetails.expired && !roomDetails?.isAdmin) {
    return (
      <>
        <Grid container justifyContent="center" alignItems="center" direction="column" style={{ height: '85%' }}>
          <div>
            <Typography variant="body2" style={{ fontWeight: 'bold', fontSize: '16px', padding: '1rem' }}>
              Room Ended.
            </Typography>
          </div>
        </Grid>
        <Grid item md={12} sm={12} xs={12} justifyContent="end" style={{ padding: '1rem 0' }}>
          <Grid container direction="row" alignItems="center" justifyContent="end" style={{ marginTop: '1em' }}>
            <Grid item md={12} sm={12} xs={12} justifyContent="end">
              <div className={classes.joinButtons}>
                <Button
                  variant="contained"
                  color="primary"
                  style={{ marginLeft: 'auto' }}
                  onClick={() => (window.location.href = '/')}
                >
                  Back
                </Button>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </>
    );
  }

  return (
    <>
      <Typography variant="h5" className={classes.gutterBottom}>
        Join {roomName}
      </Typography>

      <Grid container justifyContent="center">
        <Grid item md={7} sm={12} xs={12}>
          <div className={classes.localPreviewContainer}>
            <LocalVideoPreview identity={name} />
          </div>
          <div className={classes.mobileButtonBar}>
            <Hidden mdUp>
              <ToggleAudioButton className={classes.mobileButton} disabled={disableButtons} />
              <ToggleVideoButton className={classes.mobileButton} disabled={disableButtons} />
              <SettingsMenu mobileButtonClass={classes.mobileButton} />
            </Hidden>
          </div>
        </Grid>
        <Grid item md={5} sm={12} xs={12}>
          <Grid container direction="column" justifyContent="space-between" style={{ alignItems: 'normal' }}>
            <div>
              <Hidden smDown>
                <ToggleAudioButton className={classes.deviceButton} disabled={disableButtons} />
                <ToggleVideoButton className={classes.deviceButton} disabled={disableButtons} />
              </Hidden>
            </div>
          </Grid>
        </Grid>

        <Grid item md={12} sm={12} xs={12}>
          {isKrispInstalled && (
            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              style={{ marginBottom: '1em' }}
            >
              <div className={classes.toolTipContainer}>
                <Typography variant="subtitle2">Noise Cancellation</Typography>
                <Tooltip
                  title="Suppress background noise from your microphone"
                  interactive
                  leaveDelay={250}
                  leaveTouchDelay={15000}
                  enterTouchDelay={0}
                >
                  <div>
                    <InfoIconOutlined />
                  </div>
                </Tooltip>
              </div>

              <FormControlLabel
                control={
                  <Switch
                    checked={!!isKrispEnabled}
                    checkedIcon={<SmallCheckIcon />}
                    disableRipple={true}
                    onClick={toggleKrisp}
                  />
                }
                label={isKrispEnabled ? 'Enabled' : 'Disabled'}
                style={{ marginRight: 0 }}
                // Prevents <Switch /> from being temporarily enabled (and then quickly disabled) in unsupported browsers after
                // isAcquiringLocalTracks becomes false:
                disabled={isKrispEnabled && isAcquiringLocalTracks}
              />
            </Grid>
          )}
          <Divider />
        </Grid>
        {/* policy popup grid */}
        <Grid item md={12} sm={12} xs={12} style={{ marginTop: '1em' }}>
          {roomDetails.policy_ids && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={hasConsent}
                  onChange={e => setHasConsent(e.target.checked)}
                  name="consent"
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I agree to the{' '}
                  {policyContent.map((policy, index) => {
                    const isLast = index === policyContent.length - 1;
                    const isSecondLast = index === policyContent.length - 2;

                    return (
                      <span key={policy.id}>
                        <Link
                          href="#"
                          onClick={e => {
                            e.preventDefault();
                            handleOpenPolicy(policy);
                          }}
                        >
                          {policy.policy_title}
                        </Link>

                        {!isLast && <>{policyContent.length === 2 ? ' and ' : isSecondLast ? ', and ' : ', '}</>}
                      </span>
                    );
                  })}
                </Typography>
              }
            />
          )}
        </Grid>
        <Grid item md={12} sm={12} xs={12}>
          <Grid container direction="row" alignItems="center" style={{ marginTop: '1em' }}>
            <Hidden smDown>
              <Grid item md={7} sm={12} xs={12}>
                <SettingsMenu mobileButtonClass={classes.mobileButton} />
              </Grid>
            </Hidden>

            <Grid item md={5} sm={12} xs={12}>
              <div className={classes.joinButtons}>
                <Button variant="outlined" color="primary" onClick={() => (window.location.href = '/')}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  data-cy-join-now
                  onClick={handleJoin}
                  disabled={roomDetails?.policy_ids ? disableButtons : false}
                >
                  Join Now
                </Button>
              </div>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {isPopupOpen && (
        <PolicyPopup
          open={isPopupOpen}
          title={policyData?.policy_title}
          htmlDescription={policyData?.policy_description}
          onClose={closePopup}
        />
      )}
    </>
  );
}
